import React, { useEffect, useRef } from "react";

interface AudioWaveformProps {
  analyser: AnalyserNode | null;
  isActive: boolean;
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  analyser,
  isActive,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser || !isActive) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Use frequency data instead of time domain data
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const previousData = new Array(bufferLength).fill(0);

    const draw = () => {
      if (!ctx || !analyser) return;

      // Get frequency data
      analyser.getByteFrequencyData(dataArray);

      // Smooth the data (with a lower smoothing factor for more responsiveness)
      for (let i = 0; i < dataArray.length; i++) {
        previousData[i] = previousData[i] * 0.4 + dataArray[i] * 0.6;
      }

      // Clear canvas
      ctx.fillStyle = "rgb(249, 250, 251)"; // bg-gray-50
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Number of bars to show
      const barCount = 32;
      const barWidth = (canvas.width / barCount) * 0.8; // Leave 20% gap between bars
      const gap = (canvas.width / barCount) * 0.2;

      // Aggregate frequency data into bars
      for (let i = 0; i < barCount; i++) {
        const startIndex = Math.floor((i * bufferLength) / barCount);
        const endIndex = Math.floor(((i + 1) * bufferLength) / barCount);
        let sum = 0;

        // Average the frequency values for this bar
        for (let j = startIndex; j < endIndex; j++) {
          sum += previousData[j];
        }
        const average = sum / (endIndex - startIndex);

        // Draw bar
        const barHeight = (average / 255.0) * canvas.height;
        const x = i * (barWidth + gap);
        const y = canvas.height - barHeight;

        // Create gradient for each bar
        const gradient = ctx.createLinearGradient(x, y, x, canvas.height);
        if (isActive) {
          gradient.addColorStop(0, "rgba(34, 197, 94, 0.8)"); // green-500 with opacity
          gradient.addColorStop(1, "rgba(34, 197, 94, 0.3)");
        } else {
          gradient.addColorStop(0, "rgba(156, 163, 175, 0.8)"); // gray-400 with opacity
          gradient.addColorStop(1, "rgba(156, 163, 175, 0.3)");
        }

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [analyser, isActive]);

  return (
    <canvas
      ref={canvasRef}
      className="h-8 rounded-md bg-gray-50"
      width={200}
      height={32}
    />
  );
};

export default AudioWaveform;
